import React, { useState } from 'react'
import { Modal, Form, Steps, Button, Space, message } from 'antd'
import { BusinessDetailsStep } from './signup-steps/BusinessDetailsStep'
import { OwnerDetailsStep } from './signup-steps/OwnerDetailsStep'
import { AddressStep } from './signup-steps/AddressStep'
import { BankDetailsStep } from './signup-steps/BankDetailsStep'

interface MerchantFormModalProps {
    open: boolean
    onCancel: () => void
    onSubmit: (values: any) => Promise<void> | void
    isSubmitting?: boolean
}

const steps = [
    { title: 'Business', content: <BusinessDetailsStep /> },
    { title: 'Owner', content: <OwnerDetailsStep /> },
    { title: 'Address', content: <AddressStep /> },
    { title: 'Bank & KYC', content: <BankDetailsStep /> },
]

export const MerchantFormModal: React.FC<MerchantFormModalProps> = ({
    open,
    onCancel,
    onSubmit,
    isSubmitting
}) => {
    const [current, setCurrent] = useState(0)
    const [form] = Form.useForm()

    const stepFields = [
        ['business_name', 'business_type', 'business_constitution', 'pan_number', 'pan_document', 'gst_number', 'gst_document', 'entrance_image'],
        ['owner_name', 'email', 'mobile'],
        ['address_line1', 'city', 'state', 'pincode', 'latitude', 'longitude'],
        ['account_holder_name', 'bank_name', 'account_number', 'ifsc_code', 'account_type', 'cheque_image']
    ]

    const next = async () => {
        try {
            await form.validateFields(stepFields[current])
            setCurrent(current + 1)
        } catch (error) {
            // Form validation failed, error messages will show automatically
        }
    }

    const prev = () => {
        setCurrent(current - 1)
    }

    const onStepChange = async (target: number) => {
        // Only allow moving forward if all fields on the current step are valid
        if (target > current) {
            try {
                await form.validateFields(stepFields[current])
            } catch (error) {
                message.error('Please complete all required fields correctly before continuing.')
                return
            }
        }
        setCurrent(target)
    }

    const handleOk = async () => {
        try {
            // Validate only the current (last) step
            await form.validateFields(stepFields[current])

            // Extract all fields from the form (including unmounted ones since preserve=true)
            const allValues = form.getFieldsValue(true)

            // Since we are not doing FormData properly because of API Gateway limitations,
            // we will just submit the raw JSON values. File objects will be ignored.
            // Await the onSubmit (mutateAsync) so any API error throws into the catch block
            await onSubmit(allValues)

            // If success, reset and go to step 0
            form.resetFields()
            setCurrent(0)
        } catch (error: any) {
            // Check if error is from validation vs API
            if (error.errorFields) {
                message.error('Please complete all required fields correctly.')
            } else {
                // The API mutation error is already handled by the hook's onError,
                // we just want to catch the rejection here to PREVENT form reset.
            }
        }
    }

    const handleCancel = () => {
        form.resetFields()
        setCurrent(0)
        onCancel()
    }

    return (
        <Modal
            title="Add New Merchant (Signup Wizard)"
            open={open}
            onCancel={handleCancel}
            width={800}
            footer={null} // Custom footer handles next/prev/submit
            destroyOnHidden
        >
            <Steps current={current} onChange={onStepChange} items={steps.map(item => ({ key: item.title, title: item.title }))} style={{ marginBottom: 24 }} />

            <div style={{ minHeight: 400, padding: '20px 0' }}>
                <Form
                    form={form}
                    layout="vertical"
                    preserve={true} // ensure fields unmounted aren't destroyed from state
                >
                    {current === 0 && steps[0].content}
                    {current === 1 && steps[1].content}
                    {current === 2 && steps[2].content}
                    {current === 3 && steps[3].content}
                </Form>
            </div>

            <div style={{ marginTop: 24, textAlign: 'right' }}>
                <Space>
                    {current > 0 && (
                        <Button style={{ margin: '0 8px' }} onClick={() => prev()}>
                            Previous
                        </Button>
                    )}
                    {current < steps.length - 1 && (
                        <Button type="primary" onClick={() => next()}>
                            Next
                        </Button>
                    )}
                    {current === steps.length - 1 && (
                        <Button type="primary" onClick={handleOk} loading={isSubmitting}>
                            Submit
                        </Button>
                    )}
                </Space>
            </div>
        </Modal>
    )
}

